import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Users2,
  Star,
  BadgeCheck,
  Archive,
  MoreHorizontal,
  Pencil,
  UsersRound,
  ImageOff,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Tag, Spinner, EmptyState } from "@/components/shared";
import { CommunityDialog } from "@/components/CommunityDialog";
import { ConfirmDialog } from "@/components/dialogs";

export default function Communities() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.communities();
      const list = Array.isArray(res) ? res : res?.communities || [];
      setItems(list);
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setDialogOpen(true);
  };

  const archive = async () => {
    try {
      await api.archiveCommunity(archiveTarget.community_id);
      toast.success("Community archived");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const active = items.filter((c) => !c.archived);
  const archived = items.filter((c) => c.archived);

  return (
    <div>
      <PageHeader
        title="Communities"
        subtitle={`${active.length} active${archived.length ? ` · ${archived.length} archived` : ""}`}
        actions={
          <Button onClick={openNew} data-testid="new-community-btn">
            <Plus className="h-4 w-4" /> New community
          </Button>
        }
      />

      {error && (
        <div className="rounded-xl bg-destructive/15 ring-1 ring-destructive/40 px-4 py-3 text-sm mb-4" data-testid="communities-error">
          {error}
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : items.length === 0 && !error ? (
        <EmptyState icon={UsersRound} title="No communities yet" hint="Create your first community to get started." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="communities-grid">
          {items.map((c) => (
            <div
              key={c.community_id}
              onClick={() => navigate(`/mypersonalworkplace/communities/${c.community_id}`, { state: { community: c } })}
              className={`rounded-2xl bg-card ring-1 ring-border/70 overflow-hidden cursor-pointer hover:ring-primary/40 transition-all ${c.archived ? "opacity-60" : ""}`}
              data-testid={`community-${c.community_id}`}
            >
              <div className="h-28 bg-secondary relative">
                {c.cover_url ? (
                  <img src={c.cover_url} alt={c.name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className="h-full w-full grid place-items-center text-muted-foreground">
                    <ImageOff className="h-6 w-6" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1.5">
                  {c.featured && <Tag tone="amber"><Star className="h-3 w-3" />Featured</Tag>}
                  {c.archived && <Tag tone="muted"><Archive className="h-3 w-3" />Archived</Tag>}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-lg truncate">{c.name}</div>
                    {c.category && <div className="text-xs text-muted-foreground">{c.category}</div>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-1" onClick={(e) => e.stopPropagation()} data-testid={`community-menu-${c.community_id}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(c)} data-testid={`community-edit-${c.community_id}`}>
                        <Pencil className="h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      {!c.archived && (
                        <DropdownMenuItem onClick={() => setArchiveTarget(c)} className="text-red-300 focus:text-red-200" data-testid={`community-archive-${c.community_id}`}>
                          <Archive className="h-4 w-4" /> Archive
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {c.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.description}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <Tag tone="blue"><Users2 className="h-3 w-3" />{c.member_count ?? 0} members</Tag>
                  {c.verified_only && <Tag tone="green"><BadgeCheck className="h-3 w-3" />Verified only</Tag>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <CommunityDialog open={dialogOpen} onOpenChange={setDialogOpen} community={editing} onDone={load} />
      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(v) => !v && setArchiveTarget(null)}
        title={`Archive “${archiveTarget?.name}”?`}
        description="This soft-deletes the community. Members will lose access but data is retained."
        confirmText="Archive"
        destructive
        onConfirm={archive}
      />
    </div>
  );
}
